import React, { useMemo, useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import {
  CaretUpDown,
  Check,
  Tag,
  Buildings,
  House,
  Wrench,
  Sliders,
  Prohibit,
} from '@phosphor-icons/react';
import { ReportFieldMetadata, ReportDimension } from 'shared/types/asset-report';
import { getEntityLabel } from '../constants';

interface DimensionSelectorProps {
  label: string;
  fields: ReportFieldMetadata[];
  value: ReportDimension | null;
  onChange: (dimension: ReportDimension | null) => void;
}


const DATE_GROUPINGS = [
  { key: 'year' as const, label: 'per jaar' },
  { key: 'quarter' as const, label: 'per kwartaal' },
  { key: 'month' as const, label: 'per maand' },
  { key: 'week' as const, label: 'per week' },
];

// User-friendly groups with icons
interface FieldGroup {
  key: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  fields: string[];
}

const FIELD_GROUPS: FieldGroup[] = [
  {
    key: 'voorziening',
    label: 'Voorzieningen',
    description: 'Categorie, type, status',
    icon: <Tag className="h-4 w-4" />,
    fields: ['assetCategory.name', 'assetType.name', 'status', 'installationDate', 'warrantyExpiryDate'],
  },
  {
    key: 'gebouw',
    label: 'Gebouw',
    description: 'Gebouw, stad, vennootschap',
    icon: <Buildings className="h-4 w-4" />,
    fields: [
      'building.id', 'building.googlePlaceLongAddress',
      'building.location.city.name', 'building.location.city.zip',
      'building.location.city.province', 'building.company.name',
    ],
  },
  {
    key: 'eenheid',
    label: 'Unit',
    description: 'Type en subtype',
    icon: <House className="h-4 w-4" />,
    fields: ['unit.id', 'unit.unitType', 'unit.unitSubtype'],
  },
  {
    key: 'leverancier',
    label: 'Leverancier & Onderhoud',
    description: 'Installateur, onderhoudsdatum',
    icon: <Wrench className="h-4 w-4" />,
    fields: ['installer.name', 'lastMaintenanceDate', 'nextMaintenanceDate'],
  },
];

// Helper to get label for a field
const getLabel = (field: string) => getEntityLabel(field, field);

const DimensionSelector: React.FC<DimensionSelectorProps> = ({
  label,
  fields,
  value,
  onChange,
}) => {
  const [open, setOpen] = useState(false);

  // Get dynamic fields not covered by our predefined groups
  const dynamicFields = useMemo(() => {
    const predefined = new Set(FIELD_GROUPS.flatMap(g => g.fields));
    return fields.filter(f => f.category === 'dynamic' && !predefined.has(f.field));
  }, [fields]);

  // Available fields from backend (as a Set for quick lookup)
  const availableFields = useMemo(() => new Set(fields.map(f => f.field)), [fields]);

  const isDateField = (field: string) => {
    return ['installationDate', 'warrantyExpiryDate', 'lastMaintenanceDate', 'nextMaintenanceDate', 'createdAt'].includes(field);
  };

  const currentValueKey = value
    ? value.dateGrouping ? `${value.field}::${value.dateGrouping}` : value.field
    : null;

  const handleSelect = (field: string, dateGrouping?: string, customLabel?: string) => {
    const friendlyLabel = customLabel || getLabel(field) || field;
    const fullLabel = dateGrouping
      ? `${friendlyLabel} (${DATE_GROUPINGS.find(d => d.key === dateGrouping)?.label || dateGrouping})`
      : friendlyLabel;

    onChange({
      field,
      label: fullLabel,
      ...(dateGrouping ? { dateGrouping: dateGrouping as any } : {}),
    });
    setOpen(false);
  };

  const displayValue = value?.label || 'Kies een veld...';

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-[220px] justify-between h-8 text-sm font-normal"
          >
            <span className="truncate">{displayValue}</span>
            <CaretUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[340px] p-0" align="start" sideOffset={4}>
          <Command>
            <CommandInput placeholder="Zoek een veld..." />
            <CommandList className="max-h-[350px]">
              <CommandEmpty>Geen veld gevonden</CommandEmpty>

              {/* Clear option */}
              <CommandGroup>
                <CommandItem
                  value="__geen"
                  onSelect={() => { onChange(null); setOpen(false); }}
                  className="text-muted-foreground"
                >
                  <Prohibit className="mr-2 h-4 w-4" />
                  <span>Geen dimensie</span>
                  {!value && <Check className="ml-auto h-4 w-4" />}
                </CommandItem>
              </CommandGroup>

              <CommandSeparator />

              {/* Predefined groups */}
              {FIELD_GROUPS.map(group => {
                // Filter to only fields available from backend
                const groupFields = group.fields.filter(f => availableFields.has(f));
                if (groupFields.length === 0) return null;

                return (
                  <CommandGroup
                    key={group.key}
                    heading={
                      <div className="flex items-center gap-1.5">
                        {group.icon}
                        <span>{group.label}</span>
                        <span className="text-muted-foreground font-normal">— {group.description}</span>
                      </div>
                    }
                  >
                    {groupFields.map(field => {
                      if (isDateField(field)) {
                        // Date fields show sub-options for time period
                        return DATE_GROUPINGS.map(dg => {
                          const itemKey = `${field}::${dg.key}`;
                          return (
                            <CommandItem
                              key={itemKey}
                              value={`${getLabel(field)} ${dg.label}`}
                              onSelect={() => handleSelect(field, dg.key)}
                              className="pl-4"
                            >
                              <div className="flex flex-col flex-1">
                                <span className="text-sm">
                                  {getLabel(field)} <span className="text-muted-foreground">{dg.label}</span>
                                </span>
                              </div>
                              {currentValueKey === itemKey && <Check className="ml-auto h-4 w-4 text-primary" />}
                            </CommandItem>
                          );
                        });
                      }

                      return (
                        <CommandItem
                          key={field}
                          value={getLabel(field) || field}
                          onSelect={() => handleSelect(field)}
                          className="pl-4"
                        >
                          <div className="flex flex-col flex-1">
                            <span className="text-sm">{getLabel(field) || field}</span>
                          </div>
                          {currentValueKey === field && <Check className="ml-auto h-4 w-4 text-primary" />}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                );
              })}

              {/* Dynamic / custom fields */}
              {dynamicFields.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup
                    heading={
                      <div className="flex items-center gap-1.5">
                        <Sliders className="h-4 w-4" />
                        <span>Aangepaste velden</span>
                        <span className="text-muted-foreground font-normal">— velden per categorie</span>
                      </div>
                    }
                  >
                    {dynamicFields.map(f => (
                      <CommandItem
                        key={f.field}
                        value={f.label}
                        onSelect={() => handleSelect(f.field, undefined, f.label)}
                        className="pl-4"
                      >
                        <div className="flex flex-col flex-1">
                          <span className="text-sm">{f.label}</span>
                          <span className="text-xs text-muted-foreground leading-tight">
                            Aangepast veld{f.categoryId ? ` (categorie-specifiek)` : ''}
                          </span>
                        </div>
                        {currentValueKey === f.field && <Check className="ml-auto h-4 w-4 text-primary" />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DimensionSelector;
